import { DateTime } from 'luxon';
import { IconNames } from '@sendible/design-system/src';
import { JSX } from 'react/jsx-runtime';
import ChangeComponent from '../components/ChangeComponent';

// ── Platform icon mapping (design-system icon names) ──

export const PLATFORM_ICON_MAP: Record<string, IconNames> = {
  Facebook: 'facebook',
  FacebookPage: 'facebook',
  TikTok: 'tiktok',
  Instagram: 'instagram',
  LinkedIn: 'linkedin',
  Pinterest: 'pinterest',
  YouTube: 'youtube',
  X: 'x',
  Threads: 'threads',
  Bluesky: 'bluesky',
  BlueSky: 'bluesky',
  WordPress: 'wordpress',
  GoogleBusiness: 'google_my_business',
};

export const PLATFORM_COLOR_ICON_MAP: Record<string, IconNames> = {
  Facebook: 'facebook_color',
  FacebookPage: 'facebook_color',
  TikTok: 'tiktok_color',
  Instagram: 'instagram_color',
  LinkedIn: 'linkedin_color',
  Pinterest: 'pinterest_color',
  YouTube: 'youtube_color',
  X: 'x_color',
  Threads: 'threads_color',
  Bluesky: 'bluesky_color',
  BlueSky: 'bluesky_color',
  WordPress: 'wordpress_color',
  GoogleBusiness: 'google_my_business_color',
  // lowercase variants returned by the v1.0 filters endpoint
  'facebook(page)': 'facebook_color',
  instagrampost: 'instagram_color',
  instagrambusiness: 'instagram_color',
  linkedincompany: 'linkedin_color',
  linkedinoauth: 'linkedin_color',
  pinterestoauth: 'pinterest_color',
  tiktokbusiness: 'tiktok_color',
  youtube: 'youtube_color',
  threads: 'threads_color',
  bluesky: 'bluesky_color',
  twitteroauth: 'x_color',
  googlemybusinesslocation: 'google_my_business_color',
  wordpress: 'wordpress_color',
  'wordpress(com)': 'wordpress_color',
};

// ── Platform display name mapping ──
export const PLATFORM_DISPLAY_NAME_MAP: Record<string, string> = {
  Facebook: 'Facebook',
  FacebookPage: 'Facebook',
  'facebook(page)': 'Facebook',
  TikTok: 'TikTok',
  tiktokbusiness: 'TikTok',
  Instagram: 'Instagram',
  instagrampost: 'Instagram',
  instagrambusiness: 'Instagram',
  LinkedIn: 'LinkedIn',
  linkedincompany: 'LinkedIn',
  linkedinoauth: 'LinkedIn',
  Pinterest: 'Pinterest',
  pinterestoauth: 'Pinterest',
  YouTube: 'YouTube',
  youtube: 'YouTube',
  X: 'X (Twitter)',
  twitteroauth: 'X (Twitter)',
  Threads: 'Threads',
  threads: 'Threads',
  Bluesky: 'Bluesky',
  BlueSky: 'Bluesky',
  bluesky: 'Bluesky',
  WordPress: 'WordPress',
  wordpress: 'WordPress',
  'wordpress(com)': 'WordPress',
  GoogleBusiness: 'Google Business',
  googlemybusinesslocation: 'Google Business',
};

export const formatRelativeTime = (dateString: string): string => DateTime.fromISO(dateString).toRelative() ?? '';

/**
 * Format a number into a compact human-readable string.
 * e.g., 1500 → "1.5K", 2300000 → "2.3M"
 */
export const formatCompact = (n: number): string => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  if (n < 1000) return n.toFixed(0);

  return String(n);
};

/**
 * Format a date string into a short label.
 * e.g., "2026-03-12" → "Mar 12"
 */
export const formatDateLabel = (dateStr: string): string => {
  const d = DateTime.fromISO(dateStr, { zone: 'utc' });

  return d.toFormat('MMM d');
};

/**
 * Format a date range for display in the toolbar.
 */
export const formatPeriodDisplay = (startDate: string, endDate: string): string => {
  const start = DateTime.fromISO(startDate, { zone: 'utc' });
  const end = DateTime.fromISO(endDate, { zone: 'utc' });

  const startStr = start.toFormat('dd MMM yyyy');
  const endStr = end.toFormat('dd MMM yyyy');

  return `${startStr} – ${endStr}`;
};

/**
 * Get a YYYY-MM-DD date string offset by daysAgo from today in the user's account timezone.
 * Mirrors the Calendar's getTodayISO pattern — uses user.timezone (minutes offset from UTC)
 * to avoid browser-timezone day shifts.
 */
export const getDateISO = (timezoneOffsetMinutes = 0, daysAgo = 0): string => {
  const dt = DateTime.utc().plus({ minutes: timezoneOffsetMinutes }).minus({ days: daysAgo });

  return dt.toFormat('yyyy-MM-dd');
};

export const MODULE_LABEL_KEYS: Record<ModuleType, string> = {
  tiktok_profile_views_total: 'new_reports_module_tiktok_profile_views_total',
  tiktok_followers_last_day: 'new_reports_module_tiktok_followers_last_day',
  tiktok_video_views_total: 'new_reports_module_tiktok_video_views_total',
  tiktok_interactions_total: 'new_reports_module_tiktok_interactions_total',
  tiktok_daily_profile_views_and_followers: 'new_reports_module_tiktok_daily_views_followers',
  tiktok_followers_gained_summary: 'new_reports_module_tiktok_followers_gained_summary',
  tiktok_net_followers_comparison: 'new_reports_module_tiktok_net_followers_comparison',
  tiktok_audience_genders_distribution: 'new_reports_module_tiktok_audience_genders',
  tiktok_audience_countries_distribution: 'new_reports_module_tiktok_audience_countries',
  facebook_page_stats: 'new_reports_module_facebook_page_stats',
  facebook_page_insights: 'new_reports_module_facebook_page_insights',
  facebook_top_posts: 'new_reports_module_facebook_top_posts',
  followers_count: 'new_reports_module_followers_count',
  account_overview: 'new_reports_module_account_overview',
  follower_growth: 'new_reports_module_follower_growth',
  audience_demographics: 'new_reports_module_audience_demographics',
  top_posts: 'new_reports_module_top_posts',
  instagram_account_stats: 'new_reports_module_instagram_account_stats',
  instagram_audience: 'new_reports_module_instagram_audience',
  instagram_top_posts: 'new_reports_module_instagram_top_posts',
};

// ── Dynamic metric translation lookup ──
// Maps metric names from the data endpoint to i18n translation keys.
// The data API returns metrics like "tiktok.profileViews" which need to be
// decoded on the frontend via this lookup table.

export const METRIC_LABEL_KEYS: Record<string, string> = {
  // Common metrics
  followerCount: 'new_reports_metric_follower_count',
  reach: 'new_reports_metric_reach',
  impressions: 'new_reports_metric_impressions',
  engagementRate: 'new_reports_metric_engagement_rate',
  dailyFollowerCount: 'new_reports_metric_daily_follower_count',
  followerGrowth: 'new_reports_metric_follower_growth',
  audienceAges: 'new_reports_metric_audience_ages',
  audienceGenders: 'new_reports_metric_audience_genders',
  audienceLocations: 'new_reports_metric_audience_locations',
  postReach: 'new_reports_metric_post_reach',
  postEngagement: 'new_reports_metric_post_engagement',
  likes: 'new_reports_metric_likes',
  comments: 'new_reports_metric_comments',
  shares: 'new_reports_metric_shares',
  profileViews: 'new_reports_metric_tiktok_profile_views',
  interactions: 'new_reports_metric_tiktok_interactions',

  // Common (cross-platform) metrics
  'common.audienceActivity': 'new_reports_metric_audience_activity',

  // TikTok metrics
  'tiktok.profileViews': 'new_reports_metric_tiktok_profile_views',
  'tiktok.followers': 'new_reports_metric_tiktok_followers',
  'tiktok.netFollowers': 'new_reports_metric_tiktok_net_followers',
  'tiktok.videoViews': 'new_reports_metric_tiktok_video_views',
  'tiktok.interactions': 'new_reports_metric_tiktok_interactions',
  'tiktok.followersGained': 'new_reports_metric_tiktok_followers_gained',
  'tiktok.audienceGenders': 'new_reports_metric_tiktok_audience_genders',
  'tiktok.audienceCountries': 'new_reports_metric_tiktok_audience_countries',
  'tiktok.audienceActivity': 'new_reports_metric_tiktok_audience_activity',
  'tiktok.likes': 'new_reports_metric_tiktok_likes',
  'tiktok.comments': 'new_reports_metric_tiktok_comments',
  'tiktok.shares': 'new_reports_metric_tiktok_shares',
  'tiktok.peakHour': 'new_reports_metric_tiktok_peak_hour',
  'tiktok.peakDay': 'new_reports_metric_tiktok_peak_day',
  'tiktok.totalPublishedVideos': 'new_reports_metric_tiktok_total_published_videos',
  'tiktok.mostFrequentDay': 'new_reports_metric_tiktok_most_frequent_day',
  'tiktok.mostFrequentTime': 'new_reports_metric_tiktok_most_frequent_time',
  'tiktok.bestPerformingDate': 'new_reports_metric_tiktok_best_performing_date',
  'tiktok.totalViews': 'new_reports_metric_tiktok_total_views',
  'tiktok.totalViews.previous': 'new_reports_metric_tiktok_total_views_previous',
  'tiktok.totalViews.growth': 'new_reports_metric_tiktok_total_views_growth',
  'tiktok.bestEngagingDate': 'new_reports_metric_tiktok_best_engaging_date',
  'tiktok.totalEngagement': 'new_reports_metric_tiktok_total_engagement',
  'tiktok.totalEngagement.previous': 'new_reports_metric_tiktok_total_engagement_previous',
  'tiktok.totalEngagement.growth': 'new_reports_metric_tiktok_total_engagement_growth',
  'tiktok.video.interactions': 'new_reports_metric_tiktok_video_interactions',
  'tiktok.video.views': 'new_reports_metric_tiktok_video_views_per_video',
  'tiktok.video.likes': 'new_reports_metric_tiktok_video_likes',
  'tiktok.video.comments': 'new_reports_metric_tiktok_video_comments',
  'tiktok.video.shares': 'new_reports_metric_tiktok_video_shares',
  'tiktok.video.impressionsForYou': 'new_reports_metric_tiktok_video_impressions_for_you',
  'tiktok.video.impressionsFollow': 'new_reports_metric_tiktok_video_impressions_follow',
  'tiktok.video.impressionsHashtag': 'new_reports_metric_tiktok_video_impressions_hashtag',
  'tiktok.video.impressionsPersonalProfile': 'new_reports_metric_tiktok_video_impressions_personal_profile',
  'tiktok.video.impressionsSearch': 'new_reports_metric_tiktok_video_impressions_search',
  'tiktok.video.impressionsSound': 'new_reports_metric_tiktok_video_impressions_sound',
  'tiktok.video.impressionsOther': 'new_reports_metric_tiktok_video_impressions_other',
  'tiktok.video.avgTimeWatched': 'new_reports_metric_tiktok_video_avg_time_watched',
  'tiktok.video.totalTimeWatched': 'new_reports_metric_tiktok_video_total_time_watched',
  'tiktok.video.fullWatchedRate': 'new_reports_metric_tiktok_video_full_watched_rate',
  'TikTok.ProfileViews': 'new_reports_metric_tiktok_profile_views',
  'TikTok.Followers': 'new_reports_metric_tiktok_followers',
  'TikTok.NetFollowers': 'new_reports_metric_tiktok_net_followers',
  'TikTok.VideoViews': 'new_reports_metric_tiktok_video_views',
  'TikTok.Interactions': 'new_reports_metric_tiktok_interactions',
  'TikTok.FollowersGained': 'new_reports_metric_tiktok_followers_gained',
  'TikTok.AudienceGenders': 'new_reports_metric_tiktok_audience_genders',
  'TikTok.AudienceCountries': 'new_reports_metric_tiktok_audience_countries',

  // Facebook metrics
  'Facebook.followerCount': 'new_reports_metric_facebook_follower_count',
  'Facebook.reach': 'new_reports_metric_facebook_reach',
  'Facebook.impressions': 'new_reports_metric_facebook_impressions',
  'Facebook.engagement': 'new_reports_metric_facebook_engagement',
  'Facebook.engagementRate': 'new_reports_metric_facebook_engagement_rate',
  'Facebook.postReach': 'new_reports_metric_facebook_post_reach',
  'Facebook.postEngagement': 'new_reports_metric_facebook_post_engagement',
  'Facebook.likes': 'new_reports_metric_facebook_likes',
  'Facebook.comments': 'new_reports_metric_facebook_comments',
  'Facebook.shares': 'new_reports_metric_facebook_shares',

  // Instagram metrics
  'Instagram.followerCount': 'new_reports_metric_instagram_follower_count',
  'Instagram.reach': 'new_reports_metric_instagram_reach',
  'Instagram.impressions': 'new_reports_metric_instagram_impressions',
  'Instagram.profileViews': 'new_reports_metric_instagram_profile_views',
  'Instagram.audienceAges': 'new_reports_metric_instagram_audience_ages',
  'Instagram.audienceGenders': 'new_reports_metric_instagram_audience_genders',
  'Instagram.audienceLocations': 'new_reports_metric_instagram_audience_locations',
  'Instagram.postReach': 'new_reports_metric_instagram_post_reach',
  'Instagram.postImpressions': 'new_reports_metric_instagram_post_impressions',
  'Instagram.likes': 'new_reports_metric_instagram_likes',
  'Instagram.comments': 'new_reports_metric_instagram_comments',
  'Instagram.saves': 'new_reports_metric_instagram_saves',
};

/**
 * Resolve a metric name from the data API to a translated label.
 * Falls back to a humanised version of the metric name if no translation key is found.
 */
export const resolveMetricLabel = (metricName: string, t: (key: string) => string): string => {
  const key = METRIC_LABEL_KEYS[metricName];

  if (key) {
    const translated = t(key);

    if (translated !== key) return translated;
  }

  // Fallback: humanise the metric name (e.g. "tiktok.profileViews" → "Profile Views")
  const lastPart = metricName.includes('.') ? (metricName.split('.').pop() ?? metricName) : metricName;

  return lastPart
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
};

// ── Category label translation lookup ──

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  female: 'new_reports_pie_category_female',
  male: 'new_reports_pie_category_male',
  unspecified: 'new_reports_pie_category_unspecified',
  low: 'new_reports_scatter_category_low',
  medium: 'new_reports_scatter_category_medium',
  high: 'new_reports_scatter_category_high',
};

/**
 * Resolve a category value (e.g. "female", "high") to a translated label.
 */
export const resolveCategoryLabel = (category: string, t: (key: string) => string): string => {
  const key = CATEGORY_LABEL_KEYS[category];

  if (key) {
    const translated = t(key);

    if (translated !== key) return translated;
  }

  return category.charAt(0).toUpperCase() + category.slice(1);
};

// ── Day of week translation lookup ──

const DAY_OF_WEEK_KEYS: Record<string, string> = {
  monday: 'new_reports_day_monday',
  tuesday: 'new_reports_day_tuesday',
  wednesday: 'new_reports_day_wednesday',
  thursday: 'new_reports_day_thursday',
  friday: 'new_reports_day_friday',
  saturday: 'new_reports_day_saturday',
  sunday: 'new_reports_day_sunday',
};

/**
 * Resolve a day-of-week value to a translated label.
 */
export const resolveDayOfWeek = (day: string, t: (key: string) => string): string => {
  const key = DAY_OF_WEEK_KEYS[day.toLowerCase()];

  if (key) {
    const translated = t(key);

    if (translated !== key) return translated;
  }

  return day.charAt(0).toUpperCase() + day.slice(1);
};

/**
 * Format an hour value (0-23) into a human-readable time (e.g. "9 PM").
 */
export const formatHour = (hour: number): string => {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;

  return `${hour - 12} PM`;
};

/**
 * Get img svg for country flag
 */
export const countryCodeToFlag = (code: string): JSX.Element => {
  const parsedCode = code.toLowerCase() === 'others' ? 'un' : code.toLowerCase();

  return (
    <img
      src={`/img/flags/${parsedCode}.svg`}
      alt={code}
    />
  );
};

/**
 * Resolve a country code to a display name using Intl.DisplayNames.
 */
export const resolveCountryName = (code: string): string => {
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

    return displayNames.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
};

/**
 * Format a table cell value based on its column/metric type.
 */
export const formatTableCellValue = (
  value: TableCellValue,
  type: MetricValueType | string | undefined,
  t: (key: string) => string
): string | JSX.Element => {
  if (value === null || value === undefined) return '—';

  switch (type) {
    case 'percentage':
      return `${(Number(value) * 100).toFixed(1)}%`;
    case 'number':
      return formatCompact(Number(value));
    case 'dayOfWeek':
      return resolveDayOfWeek(String(value), t);
    case 'time':
      return String(value);
    case 'hour':
      return formatHour(Number(value));
    case 'date':
      return formatDateLabel(String(value));
    case 'country':
      return resolveCountryName(String(value));
    case 'countryFlagIcon':
      return countryCodeToFlag(String(value));
    case 'delta':
      if (typeof value === 'object' && value !== null && 'difference' in value) {
        const isPositive = (value.difference ?? 0) >= 0;
        const delta = value as DeltaValue;
        const sign = isPositive ? '+' : '';

        return (
          <ChangeComponent
            isPositive={isPositive}
            formattedDiff={`${sign}${formatCompact(delta.difference)}`}
            formattedPct={`${delta.percentage >= 0 ? '+' : ''}${delta.percentage.toFixed(1)}%`}
          />
        );
      }

      return String(value);
    case 'auto':
    default:
      if (typeof value === 'object' && value !== null && 'difference' in value) {
        const delta = value as DeltaValue;
        const isPositive = delta.difference >= 0;
        const sign = isPositive ? '+' : '';

        return (
          <ChangeComponent
            isPositive={isPositive}
            formattedDiff={`${sign}${formatCompact(delta.difference)}`}
            formattedPct={`${isPositive ? '+' : ''}${delta.percentage.toFixed(1)}%`}
          />
        );
      }

      if (typeof value === 'number') return formatCompact(value);

      return String(value);
  }
};

export const extractProfileData = <T,>(response: ModuleReportResponse | undefined | null): T[] => {
  if (!response?.profiles) return [];

  return response.profiles.filter((p): p is ModuleProfileReportResponse & { data: T } => p.data != null).map((p) => p.data as T);
};

/**
 * Resolve an array of profile IDs to full ReportProfile objects.
 * Unknown IDs (not found in allProfiles) get placeholder entries so they
 * still appear in the UI as "unavailable" profiles.
 */
export const resolveProfiles = (reportProfileIds: string[], allProfiles: ReportProfile[]): ReportProfile[] => {
  const normalisedReportIds = new Set(reportProfileIds.map(String));
  const resolved = allProfiles.filter((p) => normalisedReportIds.has(String(p.id)));
  const resolvedNormalisedIds = new Set(resolved.map((p) => String(p.id)));
  const unknownProfiles: ReportProfile[] = reportProfileIds
    .filter((pid) => !resolvedNormalisedIds.has(String(pid)))
    .map((pid) => ({ id: String(pid), name: String(pid), platform: 'Facebook' as PlatformType, status: 'Ready' as ProfileStatus, isUnknown: true }));

  return [...resolved, ...unknownProfiles];
};

export const chartLegendFormatter = (value: string): JSX.Element => {
  return <span style={{ color: '#000' }}>{value}</span>;
};
